// InfiniteGrid — бесконечная сетка «пола» в стиле Fusion 360.
//
// Реализация — шейдерная (на базе Fyrestar InfiniteGridHelper), а НЕ
// геометрия из линий. Почему так (см. исследование сетки Fusion):
//   - Линии считаются в фрагментном шейдере через fract() мировых координат,
//     ширина берётся через fwidth() → всегда ~1px, не мерцают и не пикселятся.
//   - major/minor рисуются двумя тонами серого (крупная клетка 50мм = 5×10мм).
//   - Fade к краям идёт ПО ALPHA (растворение в фон), а не в тёмный цвет —
//     поэтому нет «тёмного кольца по периметру», как было у drei-Grid.
//   - Оси X (красная) и Y (зелёная) подсвечены поверх серой сетки.
//
// Плоскость сетки — XY (Z вверх), как во всём редакторе (replicad XY-план).

import React, { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

const vertexShader = /* glsl */`
    varying vec3 vWorldPos;
    uniform float uDistance;
    void main() {
        // Большой квад в плоскости XY, следующий за камерой (X,Y),
        // чтобы сетка казалась бесконечной.
        vec3 pos = position * uDistance;
        pos.x += cameraPosition.x;
        pos.y += cameraPosition.y;
        vWorldPos = pos;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
    }
`;

const fragmentShader = /* glsl */`
    precision highp float;
    varying vec3 vWorldPos;

    uniform float uMinor;        // шаг мелкой клетки (мм)
    uniform float uMajor;        // шаг крупной клетки (мм)
    uniform vec3  uMinorColor;   // цвет мелких линий
    uniform vec3  uMajorColor;   // цвет крупных линий
    uniform vec3  uAxisXColor;   // ось X (красная)
    uniform vec3  uAxisYColor;   // ось Y (зелёная)
    uniform float uDistance;     // дальность fade
    uniform float uAxisWidth;    // полуширина оси в мм (для подсветки центра)

    // Покрытие линии сетки с шагом size — пиксель-перфект через fwidth.
    float gridCoverage(float size) {
        vec2 r = vWorldPos.xy / size;
        vec2 grid = abs(fract(r - 0.5) - 0.5) / fwidth(r);
        float line = min(grid.x, grid.y);
        return 1.0 - min(line, 1.0);
    }

    void main() {
        float minorC = gridCoverage(uMinor);
        float majorC = gridCoverage(uMajor);

        // Fade по расстоянию от камеры в плоскости пола — в АЛЬФУ (в фон).
        float dist = distance(cameraPosition.xy, vWorldPos.xy);
        float fade = 1.0 - min(dist / uDistance, 1.0);
        fade = pow(fade, 3.0); // мягкий длинный хвост

        // Базовый цвет: minor, поверх — major (темнее/контрастнее).
        vec3 color = mix(uMinorColor, uMajorColor, majorC);
        // Alpha: minor слабее (0.5×), major в полную силу.
        float alpha = mix(0.5 * minorC, max(minorC, majorC), majorC);

        // Оси X/Y — подсветка центральных линий поверх сетки.
        float ax = 1.0 - min(abs(vWorldPos.y) / (uAxisWidth * fwidth(vWorldPos.y) + 1e-4), 1.0);
        float ay = 1.0 - min(abs(vWorldPos.x) / (uAxisWidth * fwidth(vWorldPos.x) + 1e-4), 1.0);
        if (ax > 0.0) { color = mix(color, uAxisXColor, ax); alpha = max(alpha, ax); }
        if (ay > 0.0) { color = mix(color, uAxisYColor, ay); alpha = max(alpha, ay); }

        alpha *= fade;
        if (alpha <= 0.001) discard;
        gl_FragColor = vec4(color, alpha);
    }
`;

export default function InfiniteGrid({
    minor = 10,
    major = 50,
    distance = 3000,
    minorColor = '#c4c9ce',
    majorColor = '#9aa0a6',
    axisXColor = '#d9544d',
    axisYColor = '#5aa45a',
}) {
    const matRef = useRef();
    const { camera } = useThree();

    const uniforms = useMemo(() => ({
        uMinor:      { value: minor },
        uMajor:      { value: major },
        uMinorColor: { value: new THREE.Color(minorColor) },
        uMajorColor: { value: new THREE.Color(majorColor) },
        uAxisXColor: { value: new THREE.Color(axisXColor) },
        uAxisYColor: { value: new THREE.Color(axisYColor) },
        uDistance:   { value: distance },
        uAxisWidth:  { value: 1.5 },
    }), [minor, major, distance, minorColor, majorColor, axisXColor, axisYColor]);

    // Квад лежит в плоскости XY (по умолчанию PlaneGeometry в XY — то что нужно).
    return (
        <mesh frustumCulled={false} renderOrder={-1}>
            <planeGeometry args={[2, 2]}/>
            <shaderMaterial
                ref={matRef}
                vertexShader={vertexShader}
                fragmentShader={fragmentShader}
                uniforms={uniforms}
                transparent
                depthWrite={false}
                side={THREE.DoubleSide}
            />
        </mesh>
    );
}
